import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useClaimMember } from "../api/auth-api";
import { useUnclaimedMembers } from "../api/members-api";
import { Field, PrimaryButton } from "../components/dss/Primitives";
import { Icon } from "../components/dss/Icon";
import { useSupabaseAuth } from "../supabase/SupabaseContext";

// Not wrapped in RequireAuth: this page's whole job is to handle the state
// RequireAuth redirects *into* (a session with no claimed roster row yet),
// so it needs its own, looser guard — session required, member NOT required.
const Signup: React.FC = () => {
  const { session, member, isLoading, refreshMember, signOut } = useSupabaseAuth();
  const { data: unclaimed } = useUnclaimedMembers();
  const { mutateAsync: claimMember, isLoading: isClaiming } = useClaimMember();
  const [pickedId, setPickedId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const onConfirm = async () => {
    if (!pickedId) return;
    setError(null);
    try {
      await claimMember(pickedId);
      await refreshMember();
      navigate("/");
    } catch (err) {
      setError("Kunne ikke oprette adgang. Prøv igen.");
    }
  };

  const picked = unclaimed?.find((m) => m.id === pickedId);

  if (isLoading) return null;
  if (!session) return <Navigate to="/login" replace />;
  if (member) return <Navigate to="/" replace />;

  return (
    <div
      className="mx-auto max-w-md h-app bg-paper px-7 flex flex-col overflow-hidden"
      style={{ paddingTop: "calc(2rem + env(safe-area-inset-top))" }}
    >
      <button
        onClick={() => signOut()}
        className="font-mono text-[10.5px] tracking-[1.6px] uppercase text-ink3 flex items-center gap-1.5"
      >
        <Icon name="chevron-left" size={12} />
        <span>Log ind</span>
      </button>
      <div className="font-display text-[44px] leading-[0.9] tracking-tight font-extrabold mt-6 uppercase">
        Hvem er du<span className="text-sage">?</span>
      </div>
      <div className="text-[13.5px] text-ink2 mt-2.5 leading-relaxed max-w-[300px]">
        Vælg dit navn fra logens liste. Navnet knyttes til din mail, og du vælger det kun én gang.
      </div>

      <div className="mt-6">
        <Field label="E-mail">
          <div className="flex items-center gap-2.5 bg-card border border-line-soft rounded-lg py-3 px-3.5">
            <Icon name="envelope" variant="regular" size={14} className="text-ink3" />
            <span className="font-mono text-[14px] font-medium">{session?.user.email}</span>
          </div>
        </Field>
      </div>

      <div className="mt-5 flex-1 overflow-y-auto">
        <div className="font-mono text-[10.5px] tracking-[1.6px] uppercase text-ink3 mb-2.5">
          [ Logens medlemmer ]
        </div>
        <div className="flex flex-col gap-1.5">
          {unclaimed?.map((m) => {
            const isPicked = m.id === pickedId;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setPickedId(m.id)}
                className={`flex items-center gap-3 py-2.5 px-3 rounded-lg border text-left ${
                  isPicked ? "bg-vote-yes-soft border-vote-yes text-vote-yes-ink" : "bg-card border-line-soft"
                }`}
              >
                <div className="w-[30px] h-[30px] rounded-full bg-paper2 flex items-center justify-center text-[15px]">
                  {m.emoji}
                </div>
                <div className="flex-1 font-display text-[15px] font-bold uppercase tracking-tight">
                  {m.name}
                </div>
                {isPicked && <Icon name="check" size={14} />}
              </button>
            );
          })}
          {unclaimed?.length === 0 && (
            <div className="font-mono text-[11px] text-ink3 uppercase">
              Ingen ledige pladser — kontakt admin
            </div>
          )}
        </div>
        <div className="font-mono text-[9.5px] tracking-[1.2px] text-ink3 mt-3 uppercase">
          Står du ikke på listen? Skriv til admin
        </div>
      </div>

      {error && <div className="text-clay font-mono text-[11px] uppercase mb-2">{error}</div>}
      <div className="py-3.5">
        <PrimaryButton
          disabled={!pickedId || isClaiming}
          onClick={onConfirm}
          meta={
            <>
              <span>BEKRÆFT</span>
              <Icon name="arrow-right" size={13} />
            </>
          }
        >
          <span className="inline-flex items-center gap-2">
            <Icon name="user-plus" size={14} /> Opret{picked ? ` som ${picked.name}` : ""}
          </span>
        </PrimaryButton>
      </div>
    </div>
  );
};

export default Signup;
